import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable/index";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { reportHandledSync } from "@/lib/observability";
import { getSignInGate } from "@/lib/volunteer.functions";

export const Route = createFileRoute("/auth")({
  head: () => ({
    meta: [
      { title: "Volunteer Sign In | Warrior Volunteer" },
      {
        name: "description",
        content:
          "Sign in to the T1D Warriors volunteer workspace to reach your kits, outreach, and activity log.",
      },
      { property: "og:title", content: "Volunteer Sign In | Warrior Volunteer" },
      {
        property: "og:description",
        content: "Sign in to the T1D Warriors volunteer workspace.",
      },
      { property: "og:url", content: "https://volunteer.t1dw.org/auth" },
      { name: "robots", content: "noindex, follow" },
    ],
    links: [{ rel: "canonical", href: "https://volunteer.t1dw.org/auth" }],

  }),
  component: AuthPage,
});

function AuthPage() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<"signin" | "signup">("signin");
  const [busy, setBusy] = useState(false);
  const [blocked, setBlocked] = useState<"pending" | "no_application" | null>(null);
  const checkGate = useServerFn(getSignInGate);

  /**
   * A valid login is not the same as an approved volunteer. Anyone who is not
   * approved is signed straight back out with an explanation.
   */
  const admitOrBlock = useCallback(async () => {
    try {
      const gate = await checkGate();
      if (gate.allowed) {
        navigate({ to: "/dashboard", replace: true });
        return true;
      }
      setBlocked(gate.reason);
    } catch (error) {
      reportHandledSync(error, { feature: "auth", action: "signInGate" });
      setBlocked("pending");
    }
    await supabase.auth.signOut();
    return false;
  }, [checkGate, navigate]);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) void admitOrBlock();
    });
  }, [admitOrBlock]);

  async function handleEmail(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const email = String(form.get("email") ?? "").trim();
    const password = String(form.get("password") ?? "");
    setBusy(true);
    setBlocked(null);
    try {
      if (mode === "signup") {
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: { emailRedirectTo: `${window.location.origin}/auth` },
        });
        if (error) throw error;
        toast.success("Account created. Check your email if confirmation is required.");
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
      }
      const { data } = await supabase.auth.getSession();
      if (data.session) await admitOrBlock();
    } catch (error) {
      reportHandledSync(error, { feature: "auth", action: "handleEmail" });
      toast.error(error instanceof Error ? error.message : "Sign in failed.");
    } finally {
      setBusy(false);
    }
  }

  async function handleGoogle() {
    setBusy(true);
    setBlocked(null);
    const result = await lovable.auth.signInWithOAuth("google", {
      redirect_uri: `${window.location.origin}/auth`,
    });
    if (result.error) {
      setBusy(false);
      toast.error("Google sign in failed. Please try again.");
      return;
    }
    if (result.redirected) return;
    await admitOrBlock();
    setBusy(false);
  }

  return (
    <div className="app-light flex min-h-screen flex-col items-center justify-center bg-background px-5 py-16">
      <Link to="/" className="mb-8 inline-flex">
        <img src="/t1dw-logo.png" alt="T1D Warriors" className="h-10 w-auto" />
      </Link>


      <div className="panel w-full max-w-md p-8">
        <p className="t-eyebrow">Warrior Volunteer</p>
        <h1 className="t-h2 mt-3 text-ice">
          {mode === "signin" ? "Volunteer sign in" : "Create your account"}
        </h1>

        {blocked ? (
          <div className="mt-5 rounded-sm border border-cyan/40 bg-cyan/5 p-4 text-sm text-ice/85">
            {blocked === "pending" ? (
              <p>
                Your volunteer application is still under review. We will email you as soon
                as it is approved.
              </p>
            ) : (
              <>
                <p>We could not find a volunteer application for this email.</p>
                <Link
                  to="/apply"
                  className="mt-3 inline-flex font-semibold text-cyan"
                >
                  Submit an application
                </Link>
              </>
            )}
          </div>
        ) : null}



        <form onSubmit={handleEmail} className="mt-7 space-y-5">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input id="email" name="email" type="email" required autoComplete="email" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              name="password"
              type="password"
              required
              minLength={6}
              autoComplete={mode === "signin" ? "current-password" : "new-password"}
            />
          </div>
          <Button type="submit" disabled={busy} className="w-full uppercase">
            {mode === "signin" ? "Sign in" : "Sign up"}
          </Button>
          {mode === "signin" ? (
            <p className="text-center text-sm">
              <Link to="/forgot-password" className="font-semibold text-cyan">
                Forgot your password?
              </Link>
            </p>
          ) : null}
        </form>


        <div className="my-6 flex items-center gap-3">
          <span className="h-px flex-1 bg-border" />
          <span className="text-[0.65rem] tracking-wide text-muted-foreground">
            or
          </span>
          <span className="h-px flex-1 bg-border" />
        </div>

        <Button
          type="button"
          variant="outline"
          disabled={busy}
          onClick={handleGoogle}
          className="w-full uppercase"
        >
          Continue with Google
        </Button>

        <p className="mt-6 text-center text-sm text-muted-foreground">
          {mode === "signin" ? "Approved volunteer without an account yet?" : "Already have one?"}{" "}
          <button
            type="button"
            className="font-semibold text-cyan"
            onClick={() => setMode(mode === "signin" ? "signup" : "signin")}
          >
            {mode === "signin" ? "Sign up" : "Sign in"}
          </button>
        </p>
        <p className="mt-3 text-center text-sm text-muted-foreground">
          Not a volunteer yet?{" "}
          <Link to="/apply" className="font-semibold text-cyan">
            Apply here
          </Link>
        </p>
      </div>
    </div>
  );
}
